<template>
  <v-card class="pa-4">
    <h2>Closed Assignments by Adjuster (By Date)</h2>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedBranch"
          :items="branches"
          item-text="name"
          item-value="id"
          label="Branch"
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedAdjuster"
          :items="adjusterOptions"
          item-text="username"
          item-value="id"
          label="Adjuster"
          dense
          required
          :disabled="!selectedBranch"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-menu v-model="menuStart" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="startDate"
            type="date"
            @change="menuStart = false"
          />
        </v-menu>
      </v-col>
      <v-col cols="12" md="2">
        <v-menu v-model="menuEnd" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="endDate"
            type="date"
            @change="menuEnd = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <v-btn
      color="primary"
      class="mt-2 mr-2"
      :disabled="!selectedBranch || !startDate || !endDate"
      @click="fetchTable"
      :loading="loading"
    >
      <v-icon left>mdi-table</v-icon>
      Generate Table
    </v-btn>
    <v-btn
      color="success"
      class="mt-2"
      :disabled="!tableData.length"
      @click="exportExcel"
      :loading="exporting"
    >
      <v-icon left>mdi-file-excel</v-icon>
      Export Closed Assignments
    </v-btn>
    <v-alert v-if="noData" type="warning" outlined class="mt-4">
      No closed assignments found for the selected filters.
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      The exported Excel file will contain closed assignments by adjuster for
      your selected branch and date range.
    </v-alert>
    <div v-if="tableData.length" class="mt-4">
      <div class="mb-2" style="font-weight: bold; font-size: 1.1em">
        Summary of Closed Assignments
      </div>
      <v-simple-table dense>
        <thead>
          <tr>
            <th colspan="2" style="font-size: 1.1em">
              <strong>
                Total Files: {{ tableData.length }}
                for
                {{ adjusterName }} ({{ branchName }}) - Range:
                {{ formattedStartDate }} - {{ formattedEndDate }}
              </strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Insurer Breakdown -->
          <tr>
            <td colspan="2" style="background: #e3f2fd; font-weight: bold">
              Breakdown by Insurer
            </td>
          </tr>
          <tr v-for="ins in summary.insurerCounts" :key="'ins-' + ins.name">
            <td>
              Insurer: <strong>{{ ins.name }}</strong>
            </td>
            <td>{{ ins.count }}</td>
          </tr>
          <!-- Department Breakdown -->
          <tr>
            <td colspan="2" style="background: #fff3e0; font-weight: bold">
              Breakdown by Department
            </td>
          </tr>
          <tr
            v-for="dept in summary.departmentCounts"
            :key="'dept-' + dept.name"
          >
            <td>
              Department: <strong>{{ dept.name }}</strong>
            </td>
            <td>{{ dept.count }}</td>
          </tr>
          <!-- File Classification Breakdown -->
          <tr>
            <td colspan="2" style="background: #f3e5f5; font-weight: bold">
              Breakdown by File Classification
            </td>
          </tr>
          <tr v-for="fc in summary.fileClassDeptCounts" :key="'fc-' + fc.key">
            <td>
              {{ fc.dept }} - <strong>{{ fc.fileClass }}</strong>
            </td>
            <td>{{ fc.count }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </v-card>
</template>
<script>
import MemberDataService from "../../services/MemberDataService";
import BranchDataService from "../../services/BranchDataService";
import DeptDataService from "../../services/DeptDataService";
import InsDataService from "../../services/InsDataService";
import axios from "axios";
import { apiConfig } from "../../config/api.config";

export default {
  name: "ClosedByAdjusterDate",
  data() {
    return {
      selectedBranch: null,
      branches: [],
      adjusters: [],
      selectedAdjuster: "all",
      startDate: null,
      endDate: null,
      menuStart: false,
      menuEnd: false,
      exporting: false,
      loading: false,
      tableData: [],
      noData: false,
      // For mapping ids to names
      deptMap: {},
      branchMap: {},
      subDeptMap: {},
      insurerMap: {},
      statusMap: {},
      summary: {
        departments: [],
        fileClasses: [],
        insurers: [],
        branches: [],
        insurerCounts: [],
        branchCounts: [],
        departmentCounts: [],
        fileClassCounts: [],
        fileClassDeptCounts: [],
      },
    };
  },
  computed: {
    adjusterOptions() {
      // Always include "ALL ADJUSTER" at the top
      const allOption = { id: "all", username: "ALL ADJUSTER" };
      if (!this.selectedBranch) return [allOption];
      const branchObj = this.branches.find((b) => b.id === this.selectedBranch);
      if (!branchObj) return [allOption];
      const branchCode = branchObj.brCode;
      const filtered = this.adjusters
        .filter(
          (adj) =>
            Array.isArray(adj.branches) &&
            adj.branches.some((b) => b.brCode === branchCode)
        )
        .map((adj) => ({
          ...adj,
          username: (adj.username || "").toUpperCase(),
        }))
        .sort((a, b) => a.username.localeCompare(b.username));
      return [allOption, ...filtered];
    },
    adjusterName() {
      if (this.selectedAdjuster === "all") return "ALL ADJUSTER";
      const adj = this.adjusters.find((a) => a.id === this.selectedAdjuster);
      return adj ? adj.username : "";
    },
    branchName() {
      const branchObj = this.branches.find((b) => b.id === this.selectedBranch);
      return branchObj ? branchObj.name : "";
    },
    formattedStartDate() {
      return this.startDate ? this.formatDate(this.startDate) : "";
    },
    formattedEndDate() {
      return this.endDate ? this.formatDate(this.endDate) : "";
    },
  },
  mounted() {
    this.loadBranches();
    this.retrieveAdjusters();
    this.loadDeptMap();
    this.loadSubDeptMap();
    this.loadInsurerMap();
    this.loadStatusMap();
  },
  methods: {
    async loadBranches() {
      const res = await BranchDataService.getAll();
      this.branches = (res.data || [])
        .map((b) => ({
          ...b,
          name: (b.name || "").toUpperCase(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    retrieveAdjusters() {
      MemberDataService.getAll()
        .then((response) => {
          this.adjusters = response.data
            .filter(
              (item) =>
                item.roles &&
                item.roles.some((b) => b.roleCode === "adjuster") &&
                item.active
            )
            .map((item) => ({
              ...item,
              username: (item.username || "").toUpperCase(),
            }))
            .sort((a, b) => a.username.localeCompare(b.username));
        })
        .catch((e) => {
          console.log(e);
        });
    },
    async loadDeptMap() {
      try {
        const res = await DeptDataService.getAll();
        this.deptMap = {};
        (res.data || []).forEach((d) => {
          this.deptMap[d.id] = d.name ? d.name.toUpperCase() : "";
        });
      } catch {
        this.deptMap = {};
      }
    },
    async loadSubDeptMap() {
      try {
        const res = await axios.get(`${apiConfig.baseURL}/subDepts`);
        this.subDeptMap = {};
        (res.data || []).forEach((sd) => {
          this.subDeptMap[sd.id] = sd.subCode ? sd.subCode.toUpperCase() : "";
        });
      } catch {
        this.subDeptMap = {};
      }
    },
    async loadInsurerMap() {
      try {
        const res = await InsDataService.getAll();
        this.insurerMap = {};
        (res.data || []).forEach((i) => {
          this.insurerMap[i.id] = i.name ? i.name.toUpperCase() : "";
        });
      } catch {
        this.insurerMap = {};
      }
    },
    async loadStatusMap() {
      try {
        const res = await axios.get(`${apiConfig.baseURL}/stages`);
        this.statusMap = {};
        (res.data || []).forEach((s) => {
          this.statusMap[s.stageCode] = s.name ? s.name.toUpperCase() : "";
        });
      } catch {
        this.statusMap = {};
      }
    },
    async fetchTable() {
      this.loading = true;
      this.noData = false;
      this.tableData = [];
      this.summary = {
        departments: [],
        fileClasses: [],
        insurers: [],
        branches: [],
        insurerCounts: [],
        branchCounts: [],
        departmentCounts: [],
        fileClassCounts: [],
        fileClassDeptCounts: [],
      };
      try {
        // Fetch closed files for the adjuster and date range
        const params = {
          branchId: this.selectedBranch,
          adjusterId: this.selectedAdjuster,
          startDate: this.startDate,
          endDate: this.endDate,
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/closed-by-adjuster`,
          { params }
        );
        this.tableData = (res.data || []).map((f) => ({
          id: f.id,
          vehicleNo: (f.vehicleNo || "").toUpperCase(),
          dateOfAssign: f.dateOfAssign
            ? new Date(f.dateOfAssign).toLocaleDateString("en-GB")
            : "",
          dateEndInv: f.dateEndInv
            ? new Date(f.dateEndInv).toLocaleDateString("en-GB")
            : "",
          branch: this.branches.find((b) => b.id === f.branch)?.name || "",
          department: this.deptMap[f.refType] || "",
          fileClass: this.subDeptMap[f.subRefType] || "",
          status: this.statusMap[f.fileStatus] || f.fileStatus || "",
          insurer: this.insurerMap[f.insurer] || "",
          claimNo: f.claimNo || "",
          aging:
            f.dateOfAssign && f.dateEndInv
              ? Math.floor(
                  (new Date(f.dateEndInv) - new Date(f.dateOfAssign)) /
                    (1000 * 60 * 60 * 24)
                )
              : "",
        }));
        if (!this.tableData.length) {
          this.noData = true;
        } else {
          this.computeSummary();
        }
      } catch {
        this.noData = true;
      } finally {
        this.loading = false;
      }
    },
    computeSummary() {
      // Unique sets
      const departments = [];
      const fileClasses = [];
      const insurers = [];
      const branches = [];
      const departmentCounts = {};
      const fileClassCounts = {};
      const insurerCounts = {};
      const branchCounts = {};
      const fileClassDeptCounts = {};

      this.tableData.forEach((row) => {
        // Departments
        if (row.department) {
          if (!departments.includes(row.department))
            departments.push(row.department);
          departmentCounts[row.department] =
            (departmentCounts[row.department] || 0) + 1;
        }
        // File Classes
        if (row.fileClass) {
          if (!fileClasses.includes(row.fileClass))
            fileClasses.push(row.fileClass);
          fileClassCounts[row.fileClass] =
            (fileClassCounts[row.fileClass] || 0) + 1;
        }
        // Insurers
        if (row.insurer) {
          if (!insurers.includes(row.insurer)) insurers.push(row.insurer);
          insurerCounts[row.insurer] = (insurerCounts[row.insurer] || 0) + 1;
        }
        // Branches
        if (row.branch) {
          if (!branches.includes(row.branch)) branches.push(row.branch);
          branchCounts[row.branch] = (branchCounts[row.branch] || 0) + 1;
        }
        // File Classification by Department
        const dept = row.department || "";
        const fileClass = row.fileClass || "";
        if (dept && fileClass) {
          const key = `${dept}|||${fileClass}`;
          fileClassDeptCounts[key] = (fileClassDeptCounts[key] || 0) + 1;
        }
      });

      // Prepare array for fileClassDeptCounts, sorted by dept then fileClass
      const fileClassDeptCountsArr = Object.entries(fileClassDeptCounts)
        .map(([key, count]) => {
          const [dept, fileClass] = key.split("|||");
          return { key, dept, fileClass, count };
        })
        .sort((a, b) =>
          a.dept === b.dept
            ? a.fileClass.localeCompare(b.fileClass)
            : a.dept.localeCompare(b.dept)
        );

      this.summary = {
        departments,
        fileClasses,
        insurers,
        branches,
        insurerCounts: Object.entries(insurerCounts).map(([name, count]) => ({
          name,
          count,
        })),
        branchCounts: Object.entries(branchCounts).map(([name, count]) => ({
          name,
          count,
        })),
        departmentCounts: Object.entries(departmentCounts).map(
          ([name, count]) => ({ name, count })
        ),
        fileClassCounts: Object.entries(fileClassCounts).map(
          ([name, count]) => ({ name, count })
        ),
        fileClassDeptCounts: fileClassDeptCountsArr,
      };
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          branchId: this.selectedBranch,
          adjusterId: this.selectedAdjuster,
          startDate: this.startDate,
          endDate: this.endDate,
        };
        const url =
          `${apiConfig.baseURL}/compliance/closed-by-adjuster/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
    formatDate(dateStr) {
      // Format as DD/MM/YYYY (16/12/2025)
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
  },
};
</script>
